import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signIn, signOut } from 'aws-amplify/auth';
import { Button } from '../ui/Button';
import { Card, CardBody } from '../ui/Card';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { landingPath } from '../../utils/isFieldTech';

/**
 * AccountClaim — Allows invited users to claim their account.
 * Flow: Set password → Auto-login → sign-in's landing (IDI-659 D1: setup is
 * started from the Dashboard's card; no browser flags).
 * 
 * URL: /claim?email=user@example.com[&code=...]
 * IDI-268: the invite code (the temporary password from the invite email) is
 * REQUIRED. The backend proves possession of it before setting a password;
 * without it, anyone could set any user's password from this page.
 */
export default function AccountClaim() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(email ? 'set_password' : 'enter_email');

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError('Email is required'); return; }
    if (!code.trim()) { setError('Enter the temporary password from your invite email'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(password)) { setError('Password must contain an uppercase letter'); return; }
    if (!/[a-z]/.test(password)) { setError('Password must contain a lowercase letter'); return; }
    if (!/[0-9]/.test(password)) { setError('Password must contain a number'); return; }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) { setError('Password must contain a special character'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }

    setLoading(true);
    setError(null);
    try {
      // IDI-268: the backend signs in with the invite code, then completes the
      // NEW_PASSWORD_REQUIRED challenge. A wrong code sets nothing.
      await api.post('/v1/auth/claim-account', { email, code: code.trim(), password });

      // Auto-login with the new password
      try { await signOut(); } catch { /* ignore */ }
      const result = await signIn({ username: email, password });

      if (result.isSignedIn) {
        const signedIn = await checkAuth();
        navigate(landingPath(signedIn), { replace: true });
      } else {
        // Fallback — redirect to login
        navigate('/login', { replace: true });
      }
    } catch (err) {
      console.error('Claim error:', err);
      if (err.response?.status === 400 && /invite code/i.test(err.response?.data?.message || err.response?.data?.error || '')) {
        setError('That email and temporary password did not match an open invite. Check your invite email, or ask your admin to resend it.');
      } else {
        setError(err.message || 'Failed to set password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 mb-4" style={{ borderRadius: 'var(--radius-md)' }}>
            <span className="text-white text-2xl font-bold">B</span>
          </div>
          <h1 style={{ font: 'var(--text-h1)', color: 'var(--color-text-primary)', margin: 0 }}>Welcome to BIDIT</h1>
          <p className="text-gray-500 mt-1">Set up your account to get started</p>
        </div>

        <Card>
          <CardBody>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Claim Your Account</h2>
            <p className="text-sm text-gray-500 mb-6">
              Choose a password to activate your account.
            </p>

            {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

            <form onSubmit={handleSetPassword} className="space-y-4">
              {step === 'enter_email' && (
                <>
                  <Input
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@company.com"
                    autoFocus
                  />
                  <Button type="button" className="w-full" onClick={() => { if (email) setStep('set_password'); else setError('Email is required'); }}>
                    Continue
                  </Button>
                </>
              )}

              {step === 'set_password' && (
                <>
                  <div className="bg-gray-50 px-3 py-2 text-sm text-gray-600" style={{ borderRadius: 'var(--radius-md)' }}>
                    Setting up account for: <span className="font-medium">{email}</span>
                  </div>

                  <Input
                    label="Temporary Password"
                    type="password"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    placeholder="From your invite email"
                    autoComplete="one-time-code"
                    autoFocus={!code}
                  />

                  <Input
                    label="Create Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Min 8 characters"
                    autoFocus={!!code}
                    autoComplete="new-password"
                  />
                  <Input
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />

                  <div className="text-xs text-gray-400 space-y-0.5">
                    <p className="font-medium text-gray-500">Password requirements:</p>
                    <ul className="list-disc pl-4">
                      <li className={password.length >= 8 ? 'text-green-600' : ''}>At least 8 characters</li>
                      <li className={/[A-Z]/.test(password) ? 'text-green-600' : ''}>Uppercase letter</li>
                      <li className={/[a-z]/.test(password) ? 'text-green-600' : ''}>Lowercase letter</li>
                      <li className={/[0-9]/.test(password) ? 'text-green-600' : ''}>Number</li>
                      <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? 'text-green-600' : ''}>Special character (!@#$%^&*)</li>
                    </ul>
                  </div>

                  <Button type="submit" loading={loading} className="w-full" size="lg">
                    {loading ? 'Setting up...' : 'Set Password & Sign In'}
                  </Button>
                </>
              )}
            </form>

            <p className="text-xs text-gray-400 text-center mt-6">
              Already have an account? <a href="/login" className="text-primary-600 hover:text-primary-800 font-medium">Sign in</a>
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
